import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { Divider } from 'primereact/divider';
import { useTranslation } from 'localization';
import NotificationHeaderMolecule from '../../molecules/NotificationHeader/NotificationHeaderMolecule';
import NotificationFilterBarMolecule from '../../molecules/NotificationFilterBar/NotificationFilterBarMolecule';
import NotificationCardMolecule, { NotificationRecord, NotificationTypeConfig } from '../../molecules/NotificationCard/NotificationCardMolecule';
import NotificationSkeletonCardAtom from '../../atoms/NotificationSkeleton/NotificationSkeletonCardAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import { useNotificationFeed } from './useNotificationFeed';
import { useNavigate } from 'react-router-dom';
import { useApiClients } from '../../../context/ApiClientsContext';
import { useUserIdentityContext } from '../../../context/UserIdentityContext';
import { NOTIFICATION_TYPE_CONFIGS } from '../../../config/notificationTypes';
import './NotificationFeedOrganism.scss';

export interface NotificationFeedOrganismProps {
  userId: string;
  itemsPerPage?: number;
  className?: string;
}

interface NotificationPreferences {
  notificationTypes: {
    sphereCreation: boolean;
    conversationApi: boolean;
  };
  notificationChannels: {
    inApp: boolean;
    email: boolean;
    sms: boolean;
  };
}

/**
 * NotificationSettingsContent - Settings form for use inside Dialog
 */
const NotificationSettingsContent: React.FC = () => {
  const { graphClient } = useApiClients();
  const { identityId: currentUserIdentityId } = useUserIdentityContext();

  const [preferences, setPreferences] = useState<NotificationPreferences>({
    notificationTypes: {
      sphereCreation: true,
      conversationApi: true,
    },
    notificationChannels: {
      inApp: true,
      email: true,
      sms: true,
    },
  });

  // Load preferences
  useEffect(() => {
    const loadPreferences = async () => {
      if (!currentUserIdentityId || !graphClient) return;

      try {
        const response = await graphClient.get(`/identities/${currentUserIdentityId}/preferences`);

        let loadedPrefs: NotificationPreferences | null = null;
        if (response.data?.success && response.data?.data?.preferences?.notifications) {
          loadedPrefs = response.data.data.preferences.notifications;
        } else if (response.data?.preferences?.notifications) {
          loadedPrefs = response.data.preferences.notifications;
        }

        // Merge loaded preferences with defaults (default to enabled)
        if (loadedPrefs) {
          setPreferences({
            notificationTypes: {
              sphereCreation: loadedPrefs.notificationTypes?.sphereCreation ?? true,
              conversationApi: loadedPrefs.notificationTypes?.conversationApi ?? true,
            },
            notificationChannels: {
              inApp: loadedPrefs.notificationChannels?.inApp ?? true,
              email: loadedPrefs.notificationChannels?.email ?? true,
              sms: loadedPrefs.notificationChannels?.sms ?? true,
            },
          });
        }
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
      }
    };

    loadPreferences();
  }, [currentUserIdentityId, graphClient]);

  // Save preferences
  const savePreferences = async (newPreferences: NotificationPreferences) => {
    if (!currentUserIdentityId || !graphClient) return;

    try {
      await graphClient.patch(`/identities/${currentUserIdentityId}/preferences`, {
        preferences: {
          notifications: newPreferences,
        },
      });
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
    }
  };

  const toggleNotificationType = (type: keyof NotificationPreferences['notificationTypes']) => {
    const newPreferences = {
      ...preferences,
      notificationTypes: {
        ...preferences.notificationTypes,
        [type]: !preferences.notificationTypes[type],
      },
    };
    setPreferences(newPreferences);
    savePreferences(newPreferences);
  };

  const toggleNotificationChannel = (channel: keyof NotificationPreferences['notificationChannels']) => {
    const newPreferences = {
      ...preferences,
      notificationChannels: {
        ...preferences.notificationChannels,
        [channel]: !preferences.notificationChannels[channel],
      },
    };
    setPreferences(newPreferences);
    savePreferences(newPreferences);
  };

  return (
    <div className="notification-settings-content">
      <div className="notification-settings-section">
        <h3>Notification Types</h3>
        <p className="notification-settings-description">Choose which types of notifications you want to receive</p>

        <div className="notification-settings-option">
          <label htmlFor="sphere-creation">
            <strong>Perspective Assignment</strong>
            <p className="notification-option-description">Get notified when you're assigned a new perspective</p>
          </label>
          <InputSwitch
            inputId="sphere-creation"
            checked={preferences.notificationTypes.sphereCreation}
            onChange={() => toggleNotificationType('sphereCreation')}
          />
        </div>

        <div className="notification-settings-option">
          <label htmlFor="conversation-api">
            <strong>Conversations</strong>
            <p className="notification-option-description">Get notified about conversation activity</p>
          </label>
          <InputSwitch
            inputId="conversation-api"
            checked={preferences.notificationTypes.conversationApi}
            onChange={() => toggleNotificationType('conversationApi')}
          />
        </div>
      </div>

      <Divider />

      <div className="notification-settings-section">
        <h3>Notification Channels</h3>
        <p className="notification-settings-description">Choose how you want to receive notifications</p>

        <div className="notification-settings-option">
          <label htmlFor="in-app">
            <strong>In-App Notifications</strong>
            <p className="notification-option-description">Receive notifications within the application</p>
          </label>
          <InputSwitch inputId="in-app" checked={preferences.notificationChannels.inApp} onChange={() => toggleNotificationChannel('inApp')} />
        </div>

        <div className="notification-settings-option">
          <label htmlFor="email">
            <strong>Email Notifications</strong>
            <p className="notification-option-description">Receive notifications via email</p>
          </label>
          <InputSwitch inputId="email" checked={preferences.notificationChannels.email} onChange={() => toggleNotificationChannel('email')} />
        </div>

        <div className="notification-settings-option">
          <label htmlFor="sms">
            <strong>SMS Notifications</strong>
            <p className="notification-option-description">Receive notifications via text message</p>
          </label>
          <InputSwitch inputId="sms" checked={preferences.notificationChannels.sms} onChange={() => toggleNotificationChannel('sms')} />
        </div>
      </div>
    </div>
  );
};

/**
 * NotificationFeedOrganism - Complete notification feed with infinite scroll
 * Combines all notification molecules and atoms into a full feed experience
 */
const NotificationFeedOrganism: React.FC<NotificationFeedOrganismProps> = ({ userId, itemsPerPage = 20, className = '' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const observerTarget = useRef<HTMLDivElement>(null);

  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const { notifications, isLoading, isLoadingMore, error, unreadCount, hasMore, fetchMore, markAsRead, markAllAsRead } = useNotificationFeed({
    itemsPerPage,
    pollInterval: 30000,
  });

  // Translate notification type configurations at runtime
  const NOTIFICATION_TYPES: NotificationTypeConfig[] = useMemo(
    () =>
      NOTIFICATION_TYPE_CONFIGS.map((config) => ({
        value: config.value,
        label: t(config.labelKey),
        icon: config.icon,
      })),
    [t]
  );

  // Filter notifications based on selected filters
  const filteredNotifications = notifications.filter((notification) => {
    if (showUnreadOnly && notification.status === 'read') {
      return false;
    }
    if (selectedTypes.length > 0 && !selectedTypes.includes(notification.type)) {
      return false;
    }
    return true;
  });

  // Get notification type config
  const getTypeConfig = (type: string): NotificationTypeConfig | undefined => {
    return NOTIFICATION_TYPES.find((config) => config.value === type);
  };

  // Handle notification click
  const handleNotificationClick = (notification: NotificationRecord) => {
    // Mark as read
    if (notification.status !== 'read') {
      markAsRead(notification.id);
    }

    // Navigate if there's an action URL in metadata
    if (notification.metadata?.actionUrl) {
      navigate(notification.metadata.actionUrl);
    } else if (notification.sphere_id) {
      navigate(`/sphere/${notification.sphere_id}`);
    }
  };

  // Handle mark all as read
  const handleMarkAllRead = async () => {
    const filters: { type?: string } = {};
    if (selectedTypes.length > 0) {
      // Only mark filtered types as read
      for (const type of selectedTypes) {
        await markAllAsRead({ type });
      }
    } else {
      // Mark all as read
      await markAllAsRead();
    }
  };

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          fetchMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, isLoadingMore, fetchMore]);

  return (
    <div className={`notification-feed-organism ${className}`}>
      <NotificationHeaderMolecule unreadCount={unreadCount} onMarkAllRead={handleMarkAllRead} onOpenSettings={() => setIsSettingsOpen(true)} />

      <NotificationFilterBarMolecule
        notificationTypes={NOTIFICATION_TYPES}
        selectedTypes={selectedTypes}
        onFilterChange={setSelectedTypes}
        showUnreadOnly={showUnreadOnly}
        onToggleUnreadOnly={() => setShowUnreadOnly(!showUnreadOnly)}
        unreadCount={unreadCount}
      />

      <div className="notification-feed-content">
        {isLoading && <NotificationSkeletonCardAtom count={5} />}

        {!isLoading && error && <EmptyStateAtom icon="pi-exclamation-triangle" title="Error loading notifications" description={error} />}

        {!isLoading && !error && filteredNotifications.length === 0 && (
          <EmptyStateAtom
            icon="pi-inbox"
            title={showUnreadOnly ? 'No unread notifications' : 'No notifications yet'}
            description={showUnreadOnly ? 'All caught up!' : 'When you receive notifications, they will appear here.'}
          />
        )}

        {!isLoading && !error && filteredNotifications.length > 0 && (
          <>
            <div className="notification-feed-list">
              {filteredNotifications.map((notification) => (
                <NotificationCardMolecule
                  key={notification.id}
                  notification={notification}
                  typeConfig={getTypeConfig(notification.type)}
                  onClick={handleNotificationClick}
                  onMarkRead={markAsRead}
                  onViewSphere={(sphereId) => navigate(`/sphere/${sphereId}`)}
                />
              ))}
            </div>

            {/* Infinite scroll trigger */}
            {hasMore && (
              <div ref={observerTarget} className="notification-feed-observer">
                {isLoadingMore && <NotificationSkeletonCardAtom count={3} />}
              </div>
            )}

            {!hasMore && filteredNotifications.length > 10 && (
              <div className="notification-feed-end">
                <p>You've reached the end</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Notification Settings Dialog */}
      <Dialog header="Notification Settings" visible={isSettingsOpen} onHide={() => setIsSettingsOpen(false)} style={{ width: '500px' }} dismissableMask modal>
        <NotificationSettingsContent />
      </Dialog>
    </div>
  );
};

export default NotificationFeedOrganism;
